import { c as _c } from "react/compiler-runtime";
import { AnimatePresence, motion } from 'framer-motion';
import { IconButton, useTranslationContext } from 'platform/components';
import { match } from 'ts-pattern';
import type { ReactElement } from 'react';
import { useIsMobileView } from '../../hooks/useIsMobileView';
import { usePresetState } from '../../hooks/usePresetState';
import { useColumnStates } from '../../store/selectors/useColumnStates';
import type { FeGridSettings } from '../../types/Api';
import type { EagColumn } from '../../types/EagColumn';
import type { EagColumnOrGroup } from '../../types/EagColumnGroup';
import type { FilterState } from '../../types/FilterState';
import type { FormToDataGridConnectorProps } from '../../types/FormToDataGridConnectorProps';
import { ToolpanelType } from '../../types/ToolpanelType';
import { getPresetStates } from '../../utils/getPresetStates';
import { ColumnsPanel } from './components/ColumnsPanel/ColumnsPanel';
import { CustomFiltersPanel } from './components/CustomFiltersPanel/CustomFiltersPanel';
import { FilterPanel } from './components/FilterPanel/FilterPanel';
import { MobileFilterPanel } from './components/MobileFilterPanel/MobileFilterPanel';
import { MobilePresetsDialog } from './components/MobilePresetsDialog/MobilePresetsDialog';
import { SettingsPanel } from './components/SettingsPanel/SettingsPanel';
export type SidebarProps = {
  columns: EagColumn[];
  columnConfig: EagColumnOrGroup[];
  filterState: FilterState;
  onFilterChange: (key: string, value: unknown) => void;
  onResetFilters: () => void;
  settings: FeGridSettings;
  onSettingsChange: (settings: Partial<FeGridSettings>) => void;
  filterComponent?: (props: FormToDataGridConnectorProps) => ReactElement;
  activePanel: ToolpanelType | null;
  onClose: () => void;
};
const SLIDE_PANEL_DEFAULT_WIDTH = 320;
const SLIDE_PANEL_FILTER_WIDTH = 'clamp(0px, 75vw, 700px)';
const SLIDE_PANEL_COLUMN_WIDTH = 'clamp(320px, 30vw, 500px)';
const SLIDE_ANIMATION = {
  initial: {
    x: '100%'
  },
  animate: {
    x: 0
  },
  exit: {
    x: '100%'
  },
  transition: {
    duration: 0.2,
    ease: 'easeInOut'
  }
};
export function Sidebar(props) {
  const $ = _c(76);
  const t = useTranslationContext();
  const isMobile = useIsMobileView();
  const presetState = usePresetState();
  const columnStates = useColumnStates();
  let t0;
  if ($[0] !== columnStates) {
    t0 = getPresetStates(columnStates);
    $[0] = columnStates;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const currentColumnSettings = t0;
  let t1;
  if ($[2] !== currentColumnSettings || $[3] !== presetState || $[4] !== props.settings) {
    t1 = (name, desc) => presetState.createPreset(name, desc, currentColumnSettings, props.settings);
    $[2] = currentColumnSettings;
    $[3] = presetState;
    $[4] = props.settings;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  const createPresetAdapter = t1;
  let t2;
  if ($[6] !== currentColumnSettings || $[7] !== presetState || $[8] !== props.settings) {
    t2 = presetId => presetState.updatePreset(presetId, currentColumnSettings, props.settings);
    $[6] = currentColumnSettings;
    $[7] = presetState;
    $[8] = props.settings;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  const updatePresetAdapter = t2;
  let t3;
  if ($[10] !== isMobile || $[11] !== props.activePanel) {
    t3 = match({
      isMobile,
      panel: props.activePanel
    }).with({
      isMobile: true
    }, _temp).with({
      panel: ToolpanelType.filter
    }, _temp2).with({
      panel: ToolpanelType.column
    }, _temp3).otherwise(_temp4);
    $[10] = isMobile;
    $[11] = props.activePanel;
    $[12] = t3;
  } else {
    t3 = $[12];
  }
  const sidebarWidth = t3;
  let t4;
  if ($[13] !== createPresetAdapter || $[14] !== isMobile || $[15] !== presetState || $[16] !== props.activePanel || $[17] !== props.columnConfig || $[18] !== props.columns || $[19] !== props.filterComponent || $[20] !== props.filterState || $[21] !== props.onFilterChange || $[22] !== props.onResetFilters || $[23] !== props.onSettingsChange || $[24] !== props.settings || $[25] !== updatePresetAdapter) {
    let t5;
    if ($[27] !== props.columnConfig || $[28] !== props.columns) {
      t5 = () => <ColumnsPanel columns={props.columns} columnConfig={props.columnConfig} />;
      $[27] = props.columnConfig;
      $[28] = props.columns;
      $[29] = t5;
    } else {
      t5 = $[29];
    }
    let t6;
    if ($[30] !== isMobile || $[31] !== props.columnConfig || $[32] !== props.filterComponent || $[33] !== props.filterState || $[34] !== props.onFilterChange || $[35] !== props.onResetFilters) {
      t6 = () => {
        if (props.filterComponent) {
          return <CustomFiltersPanel filterComponent={props.filterComponent} />;
        }
        if (isMobile) {
          return null;
        }
        return <FilterPanel columnConfig={props.columnConfig} filterState={props.filterState} onFilterChange={props.onFilterChange} onResetFilters={props.onResetFilters} />;
      };
      $[30] = isMobile;
      $[31] = props.columnConfig;
      $[32] = props.filterComponent;
      $[33] = props.filterState;
      $[34] = props.onFilterChange;
      $[35] = props.onResetFilters;
      $[36] = t6;
    } else {
      t6 = $[36];
    }
    let t7;
    if ($[37] !== props.onSettingsChange || $[38] !== props.settings) {
      t7 = () => <SettingsPanel settings={props.settings} onSettingsChange={props.onSettingsChange} />;
      $[37] = props.onSettingsChange;
      $[38] = props.settings;
      $[39] = t7;
    } else {
      t7 = $[39];
    }
    let t8;
    if ($[40] !== createPresetAdapter || $[41] !== presetState || $[42] !== updatePresetAdapter) {
      t8 = () => <MobilePresetsDialog presets={presetState.presets} activePreset={presetState.activePreset} isPresetChanged={presetState.isPresetChanged} onActivate={presetState.activatePreset} onDeactivate={presetState.deactivatePreset} onCreate={createPresetAdapter} onUpdate={updatePresetAdapter} onDelete={presetState.deletePreset} onDuplicate={presetState.duplicatePreset} onRename={presetState.renamePreset} onResetOverrides={() => presetState.setIsPresetChanged(false)} onUpdatePosition={(_presetId, from, to) => presetState.updatePresetPosition(from, to)} />;
      $[40] = createPresetAdapter;
      $[41] = presetState;
      $[42] = updatePresetAdapter;
      $[43] = t8;
    } else {
      t8 = $[43];
    }
    t4 = match(props.activePanel).with(ToolpanelType.column, t5).with(ToolpanelType.filter, t6).with(ToolpanelType.settings, t7).with(ToolpanelType.presets, t8).with(null, _temp5).exhaustive();
    $[13] = createPresetAdapter;
    $[14] = isMobile;
    $[15] = presetState;
    $[16] = props.activePanel;
    $[17] = props.columnConfig;
    $[18] = props.columns;
    $[19] = props.filterComponent;
    $[20] = props.filterState;
    $[21] = props.onFilterChange;
    $[22] = props.onResetFilters;
    $[23] = props.onSettingsChange;
    $[24] = props.settings;
    $[25] = updatePresetAdapter;
    $[26] = t4;
  } else {
    t4 = $[26];
  }
  const panelContent = t4;
  let t5;
  if ($[44] !== props.activePanel || $[45] !== t) {
    let t6;
    if ($[47] !== t) {
      t6 = () => t("page.datagrid.labels.filters");
      $[47] = t;
      $[48] = t6;
    } else {
      t6 = $[48];
    }
    let t7;
    if ($[49] !== t) {
      t7 = () => t("page.datagrid.labels.columnSetting");
      $[49] = t;
      $[50] = t7;
    } else {
      t7 = $[50];
    }
    let t8;
    if ($[51] !== t) {
      t8 = () => t("page.datagrid.labels.settings");
      $[51] = t;
      $[52] = t8;
    } else {
      t8 = $[52];
    }
    let t9;
    if ($[53] !== t) {
      t9 = () => t("page.datagrid.labels.presets");
      $[53] = t;
      $[54] = t9;
    } else {
      t9 = $[54];
    }
    t5 = match(props.activePanel).with(ToolpanelType.filter, t6).with(ToolpanelType.column, t7).with(ToolpanelType.settings, t8).with(ToolpanelType.presets, t9).with(null, _temp6).exhaustive();
    $[44] = props.activePanel;
    $[45] = t;
    $[46] = t5;
  } else {
    t5 = $[46];
  }
  const panelTitle = t5;
  const showSlidePanel = props.activePanel != null && !(isMobile && props.activePanel === ToolpanelType.filter);
  let t6;
  if ($[55] !== panelContent || $[56] !== panelTitle || $[57] !== props.activePanel || $[58] !== props.onClose || $[59] !== showSlidePanel || $[60] !== sidebarWidth) {
    t6 = showSlidePanel && <motion.div key={props.activePanel} className="absolute top-0 right-0 h-full bg-white border-l border-gray-200 shadow-lg z-(--z-header-popper) flex flex-col overflow-hidden" style={{
      width: sidebarWidth
    }} initial={SLIDE_ANIMATION.initial} animate={SLIDE_ANIMATION.animate} exit={SLIDE_ANIMATION.exit} transition={SLIDE_ANIMATION.transition} onClick={_temp7}><div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 flex-shrink-0"><span className="text-sm font-semibold">{panelTitle}</span><IconButton size="small" icon="navigation/close" onClick={props.onClose} /></div><div className="flex-1 overflow-hidden">{panelContent}</div></motion.div>;
    $[55] = panelContent;
    $[56] = panelTitle;
    $[57] = props.activePanel;
    $[58] = props.onClose;
    $[59] = showSlidePanel;
    $[60] = sidebarWidth;
    $[61] = t6;
  } else {
    t6 = $[61];
  }
  let t7;
  if ($[62] !== t6) {
    t7 = <AnimatePresence mode="wait">{t6}</AnimatePresence>;
    $[62] = t6;
    $[63] = t7;
  } else {
    t7 = $[63];
  }
  let t8;
  if ($[64] !== isMobile || $[65] !== props.activePanel || $[66] !== props.columnConfig || $[67] !== props.filterComponent || $[68] !== props.filterState || $[69] !== props.onClose || $[70] !== props.onFilterChange || $[71] !== props.onResetFilters) {
    t8 = !props.filterComponent && <MobileFilterPanel isOpen={isMobile && props.activePanel === ToolpanelType.filter} onClose={props.onClose} columnConfig={props.columnConfig} filterState={props.filterState} onFilterChange={props.onFilterChange} onResetFilters={props.onResetFilters} />;
    $[64] = isMobile;
    $[65] = props.activePanel;
    $[66] = props.columnConfig;
    $[67] = props.filterComponent;
    $[68] = props.filterState;
    $[69] = props.onClose;
    $[70] = props.onFilterChange;
    $[71] = props.onResetFilters;
    $[72] = t8;
  } else {
    t8 = $[72];
  }
  let t9;
  if ($[73] !== t7 || $[74] !== t8) {
    t9 = <>{t7}{t8}</>;
    $[73] = t7;
    $[74] = t8;
    $[75] = t9;
  } else {
    t9 = $[75];
  }
  return t9;
}
function _temp7(e) {
  return e.stopPropagation();
}
function _temp6() {
  return "";
}
function _temp5() {
  return null;
}
function _temp4() {
  return SLIDE_PANEL_DEFAULT_WIDTH;
}
function _temp3() {
  return SLIDE_PANEL_COLUMN_WIDTH;
}
function _temp2() {
  return SLIDE_PANEL_FILTER_WIDTH;
}
function _temp() {
  return "100%" as const;
}